import { chatLinkDigits } from "@rhema/client";
import { Button, CameraIcon, Sheet } from "@rhema/ui";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "../session/session";

export const NOT_A_RHEMA_LINK = "This QR code is not a Rhema link.";

type Detector = { detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]> };
type DetectorClass = new (options: { formats: string[] }) => Detector;

function browserDetector(): DetectorClass | null {
  const found = (globalThis as { BarcodeDetector?: DetectorClass }).BarcodeDetector;
  return found ?? null;
}

/** Whether this device can scan a QR code: the mobile camera, or a browser with BarcodeDetector. */
export function useCanScan(): boolean {
  const { platform } = useSession();
  return Boolean(platform.scanCode) || browserDetector() !== null;
}

/** The camera in a sheet, read with the browser's BarcodeDetector until it finds a QR code. */
function BrowserScanner({ onCode, onClose }: { onCode: (text: string) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const found = useRef(onCode);
  found.current = onCode;

  useEffect(() => {
    const Detector = browserDetector();
    if (!Detector) return;
    let stream: MediaStream | null = null;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const detector = new Detector({ formats: ["qr_code"] });
    const tick = async () => {
      if (!live || !video.current) return;
      try {
        const [code] = await detector.detect(video.current);
        if (code?.rawValue && live) {
          found.current(code.rawValue);
          return;
        }
      } catch {
        // The first frames can arrive before the video has a size.
      }
      timer = setTimeout(() => void tick(), 250);
    };
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then(async (s) => {
        stream = s;
        if (!live || !video.current) return;
        video.current.srcObject = s;
        await video.current.play().catch(() => undefined);
        void tick();
      })
      .catch(() => setError("Rhema could not use the camera. Allow camera access and try again."));
    return () => {
      live = false;
      clearTimeout(timer);
      for (const track of stream?.getTracks() ?? []) track.stop();
    };
  }, []);

  return (
    <Sheet open onClose={onClose} title="Scan a QR code" subtitle="Point the camera at a Rhema QR code">
      {error ? (
        <p role="alert" className="m-0 mt-3 text-sm text-scarlet">
          {error}
        </p>
      ) : (
        <video
          ref={video}
          muted
          playsInline
          className="mt-3 aspect-square w-full rounded-card bg-ink object-cover"
        />
      )}
      <Button variant="ghost" block className="mt-3" onClick={onClose}>
        Cancel
      </Button>
    </Sheet>
  );
}

/** A "Scan a QR code" button. A Rhema chat link opens that chat. */
export function ScanButton({ onError, onDone }: { onError: (message: string) => void; onDone?: () => void }) {
  const { platform } = useSession();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const canScan = useCanScan();
  if (!canScan) return null;

  const handle = (text: string | null) => {
    setOpen(false);
    if (!text) return;
    const digits = chatLinkDigits(text);
    if (!digits) {
      onError(NOT_A_RHEMA_LINK);
      return;
    }
    onDone?.();
    navigate(`/chat/${digits}`);
  };

  const start = async () => {
    if (platform.scanCode) handle(await platform.scanCode());
    else setOpen(true);
  };

  return (
    <>
      <Button
        variant="soft"
        block
        className="mt-2"
        icon={<CameraIcon size={18} />}
        onClick={() => void start()}
      >
        Scan a QR code
      </Button>
      {open && <BrowserScanner onCode={handle} onClose={() => setOpen(false)} />}
    </>
  );
}
